"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

declare global {
  interface Window {
    __cmsFirstLoadHandled?: boolean;
    __cmsListeners?: Array<(event?: Event) => void>;
    __cmsDomDispatchedForPath?: string;
    __cmsPatched?: boolean;
    __cmsDomSignatures?: Set<string>;
    __cmsExternalLoaded?: Set<string>; // track external scripts
  }
}

type ExternalScriptDescriptor = {
  src?: string; // external URL
  id?: string; // optional id to apply on created script tag
  async?: boolean; // override async (default true for external src if not defer)
  defer?: boolean; // allow defer
  attrs?: Record<string, string>; // arbitrary attributes to copy
  raw?: string; // raw <script>...</script> tag string (alternative to src)
};

type ExternalScriptInput = string | ExternalScriptDescriptor; // backward compatible

type ScriptGroup = { inline?: string[]; external?: ExternalScriptInput[] };

export default function GlobalScriptRunner({
  zone,
  scripts,
}: {
  zone: "header" | "afterBody" | "beforeEnd";
  scripts?: ScriptGroup;
}) {
  const pathname = usePathname();
  const hasRunRef = useRef<string | null>(null);

  const inlineScripts = scripts?.inline || [];
  const externalScripts = scripts?.external || [];

  if (inlineScripts.length === 0 && externalScripts.length === 0) return null;

  function safeBtoa(str: string): string {
    try {
      return btoa(unescape(encodeURIComponent(str)));
    } catch {
      return str;
    }
  }

  useEffect(() => {
    const isFirstLoad = !window.__cmsFirstLoadHandled;
    window.__cmsFirstLoadHandled = true;

    if (!window.__cmsPatched) {
      window.__cmsPatched = true;
      const originalAdd = document.addEventListener.bind(document);
      document.addEventListener = function (type: any, listener: any, options: any) {
        if (type === "DOMContentLoaded") {
          (window.__cmsListeners ||= []).push(listener as any);
        }
        return originalAdd(type, listener, options);
      };
    }

    if (hasRunRef.current === `${zone}-${pathname}`) return;
    hasRunRef.current = `${zone}-${pathname}`;

    const runInline = (codes: string[], defer = false) => {
      const prev = document.querySelectorAll(`script[data-runner^="inline-${zone}-"]`);
      prev.forEach((el) => el.remove());

      codes.forEach((code, index) => {
        const isDom = code.includes("DOMContentLoaded");
        const runnerId = `inline-${zone}-${isDom ? "dom" : "nonDom"}-${index}`;

        if (isDom) {
          const signature = safeBtoa(code);
          window.__cmsDomSignatures ||= new Set();
          if (window.__cmsDomSignatures.has(signature)) return;
          window.__cmsDomSignatures.add(signature);
        }

        // If inline contains a raw <script> tag, parse and inject those tags instead of executing as JS text
        const looksLikeScriptTag = /<script[\s>]/i.test(code);
        if (looksLikeScriptTag) {
          try {
            const container = document.createElement("div");
            container.innerHTML = code;
            const tags = container.querySelectorAll("script");
            tags.forEach((tag, tIndex) => {
              const s = document.createElement("script");
              s.dataset.runner = `${runnerId}-raw-${tIndex}`;
              // copy attributes
              tag.getAttributeNames().forEach((name) => {
                const val = tag.getAttribute(name);
                if (val !== null) s.setAttribute(name, val);
              });
              const providedSrc = tag.getAttribute("src");
              if (providedSrc) {
                s.src = providedSrc;
              } else {
                s.textContent = tag.textContent || "";
              }
              try {
                document.body.appendChild(s);
              } catch (err) {
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[GlobalScriptRunner] Skipped malformed inline <script> tag', err);
                }
              }
            });
          } catch (err) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[GlobalScriptRunner] Failed to parse inline <script> tag string', err);
            }
          }
          return;
        }

        const s = document.createElement("script");
        s.dataset.runner = runnerId;
        s.textContent = isDom
          ? code
          : defer
          ? `requestAnimationFrame(() => { try { ${code} } catch (err) { console.log("") } });`
          : code;
        try {
          document.body.appendChild(s);
        } catch (err) {
          // Swallow parse/append errors (e.g., when code accidentally contains markup)
          if (process.env.NODE_ENV === 'development') {
            console.warn('[GlobalScriptRunner] Suppressed inline script error', err);
          }
        }
      });
    };

    const runExternal = (items: ExternalScriptInput[]) => {
      // Keep previous semantics for plain URL strings (no dedupe across navigations)
      window.__cmsExternalLoaded ||= new Set();

      items.forEach((item, index) => {
        // Normalize item into either a raw tag string or a URL + metadata
        let rawTag: string | undefined;
        let url: string | undefined;
        let descriptor: ExternalScriptDescriptor | undefined;

        if (typeof item === "string") {
          const trimmed = item.trim();
            // Determine if it's a tag or a URL
          if (/^<script[\s>]/i.test(trimmed)) rawTag = trimmed; else url = trimmed;
        } else {
          descriptor = item;
          if (descriptor.raw) {
            rawTag = descriptor.raw.trim();
          } else if (descriptor.src) {
            url = descriptor.src;
          }
        }

        const isTag = !!rawTag;
        const baseRunnerId = `external-${zone}-${index}-${pathname}`;

        // remove same script for this zone/path (preserves old behavior)
        const old = document.querySelector(`script[data-runner="${baseRunnerId}"]`);
        if (old) old.remove();

        // Branch: legacy (URL string) OR new (full <script> tag string)
        if (!isTag && url) {
          const isRWidget = /rwidget.*readyplanet\.com/.test(url);
          if (isRWidget) {
            // Prevent duplicate load across navigations
            if (window.__cmsExternalLoaded?.has('r-widget-script')) return;
          }
          const s = document.createElement("script");
          s.src = url; // original behavior
          if (descriptor?.id) s.id = descriptor.id;
          if (isRWidget && !s.id) s.id = 'r-widget-script';
          // Respect explicit async/defer if provided
          if (descriptor?.defer) s.defer = true;
          if (descriptor?.async === false) {
            s.async = false;
          } else if (descriptor?.async === true) {
            s.async = true;
          } else if (!descriptor?.defer) {            
            s.async = isRWidget ? false : true; // r-widget needs synchronous
          }
          // Arbitrary attributes
          if (descriptor?.attrs) {
            Object.entries(descriptor.attrs).forEach(([k, v]) => {
              if (k === "src" || k === "id") return;
              s.setAttribute(k, v);
            });
          }
          s.dataset.runner = baseRunnerId;
          document.body.appendChild(s);
          if (isRWidget) {
            window.__cmsExternalLoaded?.add('r-widget-script');
          }
          return;
        }

        // New behavior: parse provided <script ...>...</script> string without changing legacy usage
        try {
          const html = rawTag as string;
          const container = document.createElement("div");
            // Using innerHTML to parse the tag safely in a detached container
          container.innerHTML = html;
          const tag = container.querySelector("script");
          if (!tag) return; // nothing to do

          const providedSrc = tag.getAttribute("src") || undefined;
          const inlineCode = (tag.textContent || "").trim();

          // Build the actual script element we will inject
          const s = document.createElement("script");
          s.dataset.runner = baseRunnerId; // keep runner id consistent

          // Copy over attributes except we manage src later and data-runner ourselves
          tag.getAttributeNames().forEach((name) => {
            if (name === "src") return; // handle after attributes copy
            if (name === "id") {
              // preserve id (some widgets locate themselves by script tag id)
              const val = tag.getAttribute(name);
              if (val) s.id = val;
              return;
            }
            const val = tag.getAttribute(name);
            if (val !== null) s.setAttribute(name, val);
          });

          const isRWidgetTag = /rwidget.*readyplanet\.com/.test(providedSrc || inlineCode);
          if (descriptor?.id && !s.id) s.id = descriptor.id;
          if (isRWidgetTag && !s.id) s.id = 'r-widget-script';

          if (providedSrc) {
            s.src = providedSrc;
            // Only force async if author did not specify async/defer explicitly
            const hasAsync = tag.hasAttribute("async");
            const hasDefer = tag.hasAttribute("defer");
            if (descriptor?.defer) s.defer = true;
            if (descriptor?.async === false) s.async = false; else if (descriptor?.async === true) s.async = true; else if (!hasAsync && !hasDefer && !descriptor?.defer) s.async = isRWidgetTag ? false : true;
          } else if (inlineCode) {
            // Inline code inside <script> tag
            s.textContent = inlineCode;
          } else {
            return; // empty script, skip
          }

          // Extra arbitrary attrs
          if (descriptor?.attrs) {
            Object.entries(descriptor.attrs).forEach(([k, v]) => {
              if (["src", "id"].includes(k)) return;
              if (!s.hasAttribute(k)) s.setAttribute(k, v);
            });
          }

          document.body.appendChild(s);
          if (isRWidgetTag) {
            window.__cmsExternalLoaded?.add('r-widget-script');
          }
        } catch (err) {
          // Fail silently to avoid breaking existing flow
          if (process.env.NODE_ENV === 'development') {
            console.error('[GlobalScriptRunner] Failed to parse external script tag string', err);
          }
        }
      });
    };

    runInline(inlineScripts, !isFirstLoad);
    runExternal(externalScripts);

    const scheduleDispatch = () => {
      if (window.__cmsDomDispatchedForPath !== pathname) {
        window.__cmsDomDispatchedForPath = pathname;
        document.dispatchEvent(new Event("DOMContentLoaded", { bubbles: true, cancelable: true }));
        // console.log(`📣 DOMContentLoaded dispatched after all zones [${zone}]`, pathname);
      }
    };

    if (isFirstLoad) {
      if (document.readyState === "interactive" || document.readyState === "complete") {
        setTimeout(scheduleDispatch, 50);
      }
    } else {
      const timer = setTimeout(scheduleDispatch, 200);
      return () => clearTimeout(timer);
    }
  }, [pathname, zone, inlineScripts, externalScripts]);

  return null;
}
